import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { emailRegex, FormState, passwordRegex } from '@opu/utils';
import { useHistory } from 'react-router-dom';
import firebaseApp, { auth } from '../../../firebase-init';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';
import AxiosInstance from '../../../helpers/AxiosInstance';

const useForm = () => {
  const { t, i18n } = useTranslation();

  let history = useHistory();

  const [formInputs, setFormInputs] = useState({ 
    bac_year: '', 
    bac_matricule: '', 
    birth_date: '',
    email: '', 
    password: '', 
    passwordConfirmation: '', 
    conditions_check: false 
  });
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(false);

  const firebaseBacRegistration = async () => {
    setLoading(true);
    setErrors(null);
    
    // Keep the entered password before any state resets
    const signupPassword = formState.inputValues.password;

    // Validate BAC year and matricule
    const bacYear = formState.inputValues.bac_year.trim();
    const bacMatricule = formState.inputValues.bac_matricule.trim();
    
    if (!bacYear || bacYear.length !== 4 || !/^\d{4}$/.test(bacYear)) {
      setErrors('ERROR_BAC_YEAR_INVALID');
      setLoading(false);
      return;
    }
    
    if (!bacMatricule || bacMatricule.length < 6) {
      setErrors('ERROR_BAC_MATRICULE_INVALID');
      setLoading(false);
      return;
    }

    let number = bacMatricule.replace(/\s+/g, '');
    if (number.startsWith(bacYear) && number.length > bacYear.length) {
      number = number.slice(bacYear.length);
    }
    const fullMatricule =
      number.length >= 12 && /^\d+$/.test(number) ? number : bacYear + number;
    
    console.log('🔍 Starting BAC registration for:', fullMatricule);
    
    try {
      // Call new BAC signup API endpoint
      console.log('🔍 Calling BAC signup API...');
      const response = await AxiosInstance.post('/auth/bac-signup', {
        bac_year: bacYear,
        bac_matricule: bacMatricule,
        full_matricule: fullMatricule,
        email: formState.inputValues.email,
        password: formState.inputValues.password
      });
      
      console.log('✅ BAC signup API response:', response);
      
      // Handle wrapped response format from wrapResult: { message: '...', data: { firebase_uid: '...', ... } }
      // The AxiosInstance interceptor returns response.data from axios, which is the wrapResult object
      // So response = { message: '...', data: { firebase_uid: '...', ... } }
      const responseData = response?.data || response;
      
      console.log('✅ Response data extracted:', responseData);
      console.log('   - Has data property:', !!response?.data);
      console.log('   - firebase_uid:', responseData?.firebase_uid);
      console.log('   - email:', responseData?.email);
      console.log('   - hasFullLibraryAccess:', responseData?.hasFullLibraryAccess);
      
      // Extract firebase_uid from response (could be in response.data or directly in response)
      const firebaseUid = responseData?.firebase_uid || response?.firebase_uid;
      const email = responseData?.email || formState.inputValues.email;
      const hasFullAccess = responseData?.hasFullLibraryAccess || false;
      
      if (firebaseUid) {
        console.log('✅ Firebase user created, signing in...');
        console.log('   - Firebase UID:', firebaseUid);
        console.log('   - Email:', email);
        console.log('   - Has Full Access:', hasFullAccess);
        
        try {
          // Sign in with email/password
          const userCredential = await auth.signInWithEmailAndPassword(
            email, 
            signupPassword
          );
          
          console.log('✅ Firebase sign-in successful');
          
          // Get token and update claims (force refresh to get new claims)
          await userCredential.user.getIdToken(true);
          
          // Redirect to home
          history.push('/');
          
          // Show success message
          Swal.fire({
            title: t('bac-signup.success.title') || 'Account Created',
            text: hasFullAccess 
              ? (t('bac-signup.success.fullAccess') || 'Your account has been created with full library access!')
              : (t('bac-signup.success.limitedAccess') || 'Your account has been created. Your subscription needs verification.'),
            icon: 'success',
            confirmButtonText: t('common.ok') || 'OK'
          });
        } catch (signInError) {
          console.error('❌ Firebase sign-in error:', signInError);
          // Even if sign-in fails, account was created, redirect to login
          Swal.fire({
            title: t('bac-signup.success.title') || 'Account Created',
            text: t('bac-signup.success.loginPrompt') || 'Your account has been created. Please log in.',
            icon: 'success',
            confirmButtonText: t('common.ok') || 'OK'
          });
          history.push('/login');
        }
      } else {
        console.log('⚠️  No firebase_uid in response');
        console.log('   - Response structure:', JSON.stringify(response, null, 2));
        // Account might still be created, redirect to login
        Swal.fire({
          title: t('bac-signup.success.title') || 'Account Created',
          text: t('bac-signup.success.message') || 'Your account has been created. Please log in.',
          icon: 'success',
          confirmButtonText: t('common.ok') || 'OK'
        });
        history.push('/login');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('❌ BAC signup error:', error);
      console.error('❌ Error message:', error?.message);
      console.error('❌ Error response:', error?.response?.data);
      console.error('❌ Error code:', error?.code);
      
      setLoading(false);
      
      // Handle network errors
      if (error?.code === 'ERR_NAME_NOT_RESOLVED' || error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error')) {
        setErrors('ERROR_NETWORK_CONNECTION');
        Swal.fire({
          title: t('error.network.title') || 'Connection Error',
          text: t('error.network.message') || 'Could not connect to server. Please check your internet connection and try again.',
          icon: 'error',
          confirmButtonText: t('common.ok') || 'OK'
        });
        return;
      }
      
      // Handle specific API errors
      if (error?.response?.data) {
        const errorData = error.response.data;
        let errorMessage = errorData.message || errorData.error || 'ERROR_SIGNUP_FAILED';
        
        // Handle array of error messages
        if (Array.isArray(errorMessage)) {
          errorMessage = errorMessage[0];
        }
        
        // Handle wrapped result format
        if (errorData.data && errorData.data.message) {
          errorMessage = errorData.data.message;
        }
        
        setErrors(errorMessage);
        
        // Show error message to user
        Swal.fire({
          title: t('error.title') || 'Error',
          text: typeof errorMessage === 'string' ? errorMessage : 'ERROR_SIGNUP_FAILED',
          icon: 'error',
          confirmButtonText: t('common.ok') || 'OK'
        });
      } else if (error?.message) {
        // Handle other errors (like Firebase errors)
        setErrors(error.message);
        Swal.fire({
          title: t('error.title') || 'Error',
          text: error.message,
          icon: 'error',
          confirmButtonText: t('common.ok') || 'OK'
        });
      } else {
        // Generic error
        setErrors('ERROR_SIGNUP_FAILED');
        Swal.fire({
          title: t('error.title') || 'Error',
          text: t('error.signupFailed') || 'Signup failed. Please try again.',
          icon: 'error',
          confirmButtonText: t('common.ok') || 'OK'
        });
      }
    }
  };

  const inputValues = {
    bac_year: { 
      value: '', 
      validate: { required: true, pattern: /^\d{4}$/ }
    },
    bac_matricule: { 
      value: '', 
      validate: { required: true }
    },
    email: { 
      value: '', 
      validate: { required: true, pattern: emailRegex }
    },
    password: { 
      value: '', 
      validate: { required: true, min: 8, pattern: passwordRegex }
    },
    password_confirm: { 
      value: '', 
      validate: { required: true }
    },
    conditions: { 
      value: true, 
      validate: { required: true }
    },
  };

  const { formState, submitHandler, field, handleErrorsServer } = FormState(inputValues, firebaseBacRegistration);

  return {
    field,
    submitHandler,
    formInputs,
    setFormInputs,
    errors,
    loading,
  };
};

export default useForm;
